import React, { useEffect, useState, useCallback } from "react";
// 💥 NEW: Import createPortal from react-dom
import { createPortal } from "react-dom";
import styled from "styled-components";
import { db } from "../firebase/config";
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  doc,
} from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { format, isSameDay } from "date-fns";
import { CheckCircle, X } from "lucide-react";

// ---------------- AddTaskModal Component (Now using Portal) ----------------

const AddTaskModal = ({ client, closeModal }) => {
  const { userId } = useAuth();
  const [newTask, setNewTask] = useState("");
  const [frequency, setFrequency] = useState("One-Time");
  const [recurringDays, setRecurringDays] = useState([]);
  const [loading, setLoading] = useState(false);

  // 💥 FIX 1: Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const toggleDay = (day) => {
    setRecurringDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // Inside ClientCard.jsx, in the AddTaskModal component:

  const addTask = useCallback(async () => {
    if (!userId || !newTask.trim()) return;
    setLoading(true);

    const isRecurring = frequency !== "One-Time";
    let daysToSave = [];

    if (frequency === "Every-Day") {
      daysToSave = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
    } else if (frequency === "Specific-Days") {
      daysToSave = recurringDays;
    }

    try {
      if (!userId) return;
      const docRef = await addDoc(
        collection(db, "users", userId, "clients", client.id, "tasks"),
        {
          text: newTask,
          completed: false,
          createdAt: new Date(),
          recurring: isRecurring,

          // 🎯 THE FIX: Change 'recurringDays' to 'days' to match dashboard logic
          days: daysToSave,

          date: isRecurring ? null : new Date(),
          completedDates: {},
        }
      );

      // Dispatch a custom event so DashboardTasks can immediately pick up the new task
      try {
        const addedTask = {
          id: docRef.id,
          text: newTask,
          completed: false,
          createdAt: new Date(),
          recurring: isRecurring,
          days: daysToSave,
          date: isRecurring ? null : new Date(),
          completedDates: {},
          pushedDates: {},
        };
        window.dispatchEvent(
          new CustomEvent("clientTaskAdded", {
            detail: { clientId: client.id, task: addedTask },
          })
        );
      } catch (err) {
        // Non-fatal: event dispatch failed
        console.warn("clientTaskAdded dispatch failed", err);
      }

      setNewTask("");
      setFrequency("One-Time");
      setRecurringDays([]);
      closeModal();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [userId, newTask, frequency, recurringDays, client.id, closeModal]);

  // FIX 2: Only close modal if the click target is the overlay itself
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // 💥 FIX 4: Use createPortal to render the modal outside the ClientCard DOM structure
  // This requires a target DOM node. We use document.body, which is universally available.
  return createPortal(
    <ModalOverlay onClick={handleOverlayClick}>
      {/* FIX 3: Stop propagation on the content box */}
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={closeModal}>
          <X size={20} />
        </CloseButton>
        <h2>Add Task for {client.name}</h2>
        <Input
          type="text"
          placeholder="Task Name (e.g., Send follow-up email)..."
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
        />

        <FrequencySelect
          value={frequency}
          onChange={(e) => {
            setFrequency(e.target.value);
            setRecurringDays([]);
          }}
        >
          <option value="One-Time">One-Time Task (Today)</option>
          <option value="Every-Day">Recurring: Every Day</option>
          <option value="Specific-Days">Recurring: Choose Specific Days</option>
        </FrequencySelect>

        {frequency === "Specific-Days" && (
          <DaysContainer>
            {[
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ].map((day) => (
              <DayButton
                key={day}
                active={recurringDays.includes(day)}
                onClick={() => toggleDay(day)}
              >
                {day.slice(0, 3)}
              </DayButton>
            ))}
          </DaysContainer>
        )}

        <Button
          onClick={addTask}
          disabled={
            loading ||
            !newTask.trim() ||
            (frequency === "Specific-Days" && recurringDays.length === 0)
          }
        >
          {loading ? "Adding..." : "Add Task"}
        </Button>
      </ModalBox>
    </ModalOverlay>,
    // The target DOM node for the portal
    document.body
  );
};

// ---------------- ClientCard Component (No change needed here for fix) ----------------

export default function ClientCard({ client, onClick }) {
  const { userId } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);

  const selectedDate = new Date();

  // Listen to tasks
  useEffect(() => {
    if (!client?.id) return;
    if (!userId) return;
    const tasksRef = collection(
      db,
      "users",
      userId,
      "clients",
      client.id,
      "tasks"
    );

    const unsubscribe = onSnapshot(tasksRef, (snapshot) => {
      const fetched = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        days: doc.data().days || doc.data().recurringDays || [],
      }));
      const sorted = fetched.sort(
        (a, b) => b.createdAt?.toDate() - a.createdAt?.toDate()
      );
      setTasks(sorted);
    });

    return () => unsubscribe();
  }, [client?.id, userId]);

  const toggleComplete = async (task) => {
    if (!userId) return;
    const taskRef = doc(
      db,
      "users",
      userId,
      "clients",
      client.id,
      "tasks",
      task.id
    );
    if (task.recurring) {
      const todayKey = format(selectedDate, "yyyy-MM-dd");
      const completedToday = task.completedDates?.[todayKey] ?? false;
      await updateDoc(taskRef, {
        [`completedDates.${todayKey}`]: !completedToday,
      });
    } else {
      await updateDoc(taskRef, { completed: !task.completed });
    }
  };

  // Inside ClientCard.jsx

  // ... (code above remains the same)

  const todayName = format(selectedDate, "EEEE"); // use full day name

  const visibleTasks = tasks.filter((task) => {
    // Check for recurring tasks
    if (task.recurring) {
      const days = task.days || [];

      // Case 1: "Every-Day" (or if no days were specified but recurring=true)
      // If no specific days are selected (days array is empty/null) OR all 7 days are selected, it's an "Every-Day" task.
      // If you used the "Every-Day" option in the modal, it saves 7 days, so this check covers both.
      if (days.length === 0 || days.length === 7) {
        return true;
      }

      // Case 2: "Specific-Days" (1 to 6 days selected)
      // This is the crucial check: task only appears if today is one of the selected days.
      return days.includes(todayName);
    }

    // One-time task logic (remains the same)
    // Check if the one-time task date matches the currently selected date.
    if (!task.date) return false;
    return isSameDay(task.date.toDate(), selectedDate);
  });

  // ... (code below remains the same)
  const totalTasks = visibleTasks.length;
  const completedTasks = visibleTasks.filter((task) => {
    const completed = task.recurring
      ? task.completedDates?.[format(selectedDate, "yyyy-MM-dd")] ?? false
      : task.completed;
    return completed;
  }).length;

  const completionPercentage = totalTasks
    ? Math.round((completedTasks / totalTasks) * 100)
    : 0;
  const allCompleted = completionPercentage === 100;

  return (
    <Card completed={allCompleted} onClick={() => onClick(client)}>
      <CompletionBadge>{completionPercentage}%</CompletionBadge>

      {allCompleted && (
        <CompletedOverlay>
          <CheckCircle size={48} color="#22c55e" />
        </CompletedOverlay>
      )}

      <Name>{client.name}</Name>
      <Info>Goal: {client.goal}</Info>

      <TaskList>
        {visibleTasks.map((task) => {
          const completed = task.recurring
            ? task.completedDates?.[format(selectedDate, "yyyy-MM-dd")] ?? false
            : task.completed;
          return (
            <TaskItem
              key={task.id}
              completed={completed}
              onClick={(e) => {
                e.stopPropagation();
                toggleComplete(task);
              }}
            >
              {task.text}
            </TaskItem>
          );
        })}
        {visibleTasks.length > 5 && (
          <Info style={{ fontSize: "0.8rem", color: "#888" }}>
            +{visibleTasks.length - 5} more...
          </Info>
        )}
      </TaskList>

      <AddTaskWrapper onClick={(e) => e.stopPropagation()}>
        <Button onClick={() => setShowAddTaskModal(true)}>
          + Add New Task
        </Button>
      </AddTaskWrapper>

      {/* The Modal Component is rendered here, but its content is projected to document.body via the Portal */}
      {showAddTaskModal && (
        <AddTaskModal
          client={client}
          closeModal={() => setShowAddTaskModal(false)}
        />
      )}
    </Card>
  );
}

// ---------------- Styles ----------------
// ... (All original styled components follow) ...

const Card = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.75rem;
  position: relative;
  transition: 0.5s ease;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    transform: scale(1.02);
  }

  ${({ completed }) => completed && `filter: blur(2px);`}
`;
const CompletionBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  font-weight: 600;
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  z-index: 10;
`;
const CompletedOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  pointer-events: none;
`;
const AddTaskWrapper = styled.div`
  z-index: 10;
`;
const Name = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
`;
const Info = styled.p`
  font-size: 0.9rem;
  color: #555;
`;
const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;
const TaskItem = styled.div`
  background: var(--thirdBackgroundColor);
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: ${({ completed }) =>
    completed ? "var(--fontSecondaryColor)" : "var(--fontPrimaryColor)"};
  text-decoration: ${({ completed }) => (completed ? "line-through" : "none")};
  transition: 0.2s;
  &:hover {
    background: var(--primaryBackgroundColor);
  }
`;
const AddTaskContainer = styled.div``;
const Row = styled.div``;
const CheckboxLabel = styled.label``;

const Button = styled.button`
  background: #495269;
  color: white;
  border: none;
  font-family: "Montserrat";
  padding: 0.75rem 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: 0.2s;
  margin-top: 0.8rem;
  &:hover {
    background: #333;
  }
`;
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
`;
const ModalBox = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 2rem;
  border-radius: 18px;
  width: 400px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
`;
const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--fontPrimaryColor);
`;
const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
`;
const FrequencySelect = styled.select`
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: white;
  font-size: 0.9rem;
  color: #333;
`;
const DaysContainer = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;
const DayButton = styled.button`
  background: ${({ active }) => (active ? "#495269" : "#eee")};
  color: ${({ active }) => (active ? "white" : "#555")};
  border: none;
  padding: 0.4rem 0.7rem;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    background: ${({ active }) => (active ? "#333" : "#ddd")};
  }
`;
